import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { palette, fontFamily } from '../../styles/tokens';

// Inline field config (mirrored from mobile clientFieldsConfig — same structure)
interface FieldDef { key: string; label: string; type: string; placeholder?: string; options?: string[]; section?: string; required?: boolean; }

const FIELDS: Record<string, FieldDef[]> = {
  beauty: [
    { key: 'hair_type', label: 'Hair type', type: 'select', options: ['Straight', 'Wavy', 'Curly', 'Coily', 'Fine', 'Thick'], section: 'Hair Profile' },
    { key: 'hair_colour', label: 'Current colour', type: 'text', placeholder: 'e.g. Level 7 golden blonde', section: 'Hair Profile' },
    { key: 'allergies', label: 'Allergies or sensitivities', type: 'textarea', placeholder: 'Latex, specific products, etc.', section: 'Health' },
    { key: 'skin_type', label: 'Skin type', type: 'select', options: ['Normal', 'Dry', 'Oily', 'Combination', 'Sensitive'], section: 'Health' },
    { key: 'service_notes', label: 'Anything we should know?', type: 'textarea', placeholder: 'Preferences, concerns, etc.', section: 'Preferences' },
  ],
  pet: [
    { key: 'pet_name', label: 'Pet name', type: 'text', placeholder: 'Buddy, Luna, etc.', section: 'Pet Info', required: true },
    { key: 'pet_species', label: 'Species', type: 'select', options: ['Dog', 'Cat', 'Bird', 'Rabbit', 'Other'], section: 'Pet Info' },
    { key: 'pet_breed', label: 'Breed', type: 'text', placeholder: 'e.g. Golden Retriever', section: 'Pet Info' },
    { key: 'pet_weight', label: 'Weight (lbs)', type: 'number', section: 'Pet Info' },
    { key: 'vaccinations', label: 'Vaccination status', type: 'textarea', placeholder: 'Rabies, DHPP, Bordetella...', section: 'Health' },
    { key: 'medical_notes', label: 'Medical conditions', type: 'textarea', section: 'Health' },
    { key: 'emergency_contact', label: 'Emergency vet', type: 'text', placeholder: 'Clinic name + phone', section: 'Emergency' },
  ],
  home_services: [
    { key: 'property_type', label: 'Property type', type: 'select', options: ['House', 'Condo', 'Townhouse', 'Apartment', 'Commercial'], section: 'Property' },
    { key: 'access_instructions', label: 'Access instructions', type: 'textarea', placeholder: 'Gate code, key location, etc.', section: 'Access' },
    { key: 'parking_notes', label: 'Parking notes', type: 'text', section: 'Access' },
  ],
  cleaning: [
    { key: 'property_type', label: 'Property type', type: 'select', options: ['House', 'Condo', 'Townhouse', 'Apartment', 'Commercial'], section: 'Property' },
    { key: 'bedrooms', label: 'Bedrooms', type: 'number', section: 'Property' },
    { key: 'bathrooms', label: 'Bathrooms', type: 'number', section: 'Property' },
    { key: 'access_instructions', label: 'Access instructions', type: 'textarea', section: 'Access' },
    { key: 'cleaning_preferences', label: 'Preferences', type: 'textarea', placeholder: 'Focus areas, products to avoid...', section: 'Preferences' },
  ],
  automotive: [
    { key: 'vehicle_make', label: 'Make', type: 'text', placeholder: 'e.g. Toyota', section: 'Vehicle' },
    { key: 'vehicle_model', label: 'Model', type: 'text', placeholder: 'e.g. Camry', section: 'Vehicle' },
    { key: 'vehicle_year', label: 'Year', type: 'number', section: 'Vehicle' },
    { key: 'vehicle_colour', label: 'Colour', type: 'text', section: 'Vehicle' },
    { key: 'licence_plate', label: 'Licence plate', type: 'text', section: 'Vehicle' },
  ],
  wellness: [
    { key: 'fitness_goals', label: 'Goals', type: 'textarea', placeholder: 'What are you looking to achieve?', section: 'About You' },
    { key: 'injuries', label: 'Injuries or limitations', type: 'textarea', section: 'Health' },
    { key: 'experience_level', label: 'Experience level', type: 'select', options: ['Beginner', 'Intermediate', 'Advanced'], section: 'About You' },
    { key: 'emergency_contact', label: 'Emergency contact', type: 'text', section: 'Emergency' },
  ],
  health: [
    { key: 'date_of_birth', label: 'Date of birth', type: 'date', section: 'Personal' },
    { key: 'allergies', label: 'Allergies', type: 'textarea', section: 'Medical' },
    { key: 'medications', label: 'Current medications', type: 'textarea', section: 'Medical' },
    { key: 'emergency_contact', label: 'Emergency contact', type: 'text', section: 'Emergency' },
  ],
  construction: [
    { key: 'property_type', label: 'Property type', type: 'select', options: ['House', 'Condo', 'Townhouse', 'Apartment', 'Commercial'], section: 'Property' },
    { key: 'access_instructions', label: 'Access instructions', type: 'textarea', section: 'Access' },
    { key: 'parking_notes', label: 'Parking notes', type: 'text', section: 'Access' },
  ],
};

const DEFAULT_FIELDS: FieldDef[] = [
  { key: 'notes', label: 'Anything we should know?', type: 'textarea', placeholder: 'Preferences, concerns, special requests...', section: 'General' },
];

type Status = 'loading' | 'form' | 'success' | 'error';

const INPUT: React.CSSProperties = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #E5E5EA', fontSize: 15, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const, color: '#1A1A1A' };
const LABEL_S: React.CSSProperties = { fontSize: 14, fontWeight: 500, color: '#636366', marginBottom: 6, display: 'block' };

export function IntakeFormPage() {
  const { businessId, clientId } = useParams<{ businessId: string; clientId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState<Status>('loading');
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [values, setValues] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect, @typescript-eslint/no-floating-promises */
  useEffect(() => {
    if (!businessId || !clientId || !token) { setStatus('error'); return; }
    (async () => {
      const { data: biz } = await supabase.from('businesses').select('name, industry').eq('id', businessId).maybeSingle();
      if (!biz) { setStatus('error'); return; }
      setBusinessName(biz.name); setIndustry(biz.industry ?? '');
      setStatus('form');
    })();
  }, [businessId, clientId, token]);
  /* eslint-enable react-hooks/set-state-in-effect, @typescript-eslint/no-floating-promises */

  const fields = FIELDS[industry] ?? DEFAULT_FIELDS;
  const sections = [...new Set(fields.map(f => f.section ?? 'General'))];

  const handleSubmit = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cf: Record<string, any> = {};
    for (const [k, v] of Object.entries(values)) { if (v != null && v !== '' && v !== false) cf[k] = v; }
    setSubmitting(true);
    const { data, error } = await supabase.rpc('submit_intake_form', { p_client_id: clientId, p_token: token, p_custom_fields: cf });
    setSubmitting(false);
    if (error || !data?.success) { if (data?.error?.includes('expired')) setStatus('error'); return; }
    setStatus('success');
  };

  if (status === 'loading') return <Page><p style={{ color: '#636366' }}>Loading...</p></Page>;
  if (status === 'error') return <Page><h2 style={{ color: '#1A1A1A', fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>Link Expired</h2><p style={{ color: '#636366', lineHeight: 1.5 }}>This form link is no longer valid. Please contact {businessName || 'your service provider'} for a new link.</p></Page>;
  if (status === 'success') return <Page><div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 20px', background: `linear-gradient(135deg, ${palette.green}, #2E7D32)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></div><h2 style={{ color: '#1A1A1A', fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>Thank You!</h2><p style={{ color: '#636366', lineHeight: 1.5 }}>Your information has been received. See you at your appointment with {businessName}.</p></Page>;

  return (
    <Page>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1A1A1A', margin: '0 0 6px' }}>Before your appointment</h2>
      <p style={{ fontSize: 14, color: '#636366', margin: '0 0 24px', lineHeight: 1.5 }}>{businessName} would like some information to prepare for your visit.</p>

      {sections.map(sec => (
        <div key={sec} style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#ADB5BD', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{sec}</div>
          {fields.filter(f => (f.section ?? 'General') === sec).map(f => {
            const inputId = `intake-${f.key}`;
            return (
              <div key={f.key} style={{ marginBottom: 14 }}>
                {f.type !== 'boolean' && (
                  <label htmlFor={inputId} style={LABEL_S}>{f.label}{f.required ? ' *' : ''}</label>
                )}
                {f.type === 'select' ? (
                  <select id={inputId} style={INPUT} value={values[f.key] ?? ''} onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}>
                    <option value="">Select...</option>
                    {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea id={inputId} style={{ ...INPUT, minHeight: 70, resize: 'vertical' }} value={values[f.key] ?? ''} onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))} placeholder={f.placeholder} />
                ) : f.type === 'boolean' ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#1A1A1A' }}>
                    <input id={inputId} type="checkbox" checked={!!values[f.key]} onChange={e => setValues(v => ({ ...v, [f.key]: e.target.checked }))} /> {f.label}{f.required ? ' *' : ''}
                  </label>
                ) : (
                  <input id={inputId} style={INPUT} type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'} value={values[f.key] ?? ''} onChange={e => setValues(v => ({ ...v, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value }))} placeholder={f.placeholder} />
                )}
              </div>
            );
          })}
        </div>
      ))}

      <button onClick={handleSubmit} disabled={submitting} style={{ width: '100%', padding: '14px 0', borderRadius: 12, border: 'none', background: palette.accent, color: '#fff', fontSize: 16, fontWeight: 600, fontFamily: 'inherit', cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1, marginTop: 8 }}>
        {submitting ? 'Submitting...' : 'Submit'}
      </button>
    </Page>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: '#F5F5F5', fontFamily, padding: 24 }}>
      <div style={{ textAlign: 'center', padding: '36px 28px', background: '#fff', borderRadius: 16, maxWidth: 440, width: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginTop: 40 }}>
        <img src="https://crewbit.ca/logo.png" alt="CrewBit" height="28" style={{ marginBottom: 20 }} />
        <div style={{ textAlign: 'left' }}>{children}</div>
      </div>
    </div>
  );
}
